/**
 * The proposal inspector — read the document, edit it, approve or reject
 * (2026-09-20). Opens from the "View" control on any proposal card that
 * carries a `preview_url`.
 *
 * Owner, looking at two byte-identical "Daily reflection awaiting approval"
 * cards: *"how can i to approve without seeing them"*. They could not: the
 * row carried a title, a generic summary and two buttons, and nothing OF
 * the reflection — no excerpt, no date, and a deep link to the whole
 * /journal module. Rule 29: an outcome the user is asked to act on must be
 * one they can SEE.
 *
 * It renders through `ReviewPanel`, the component whose own docstring says
 * it is "used across all memory approval surfaces (daily memory,
 * reflections, relationship evolution, dream beliefs)" — and which, until
 * now, exactly one page used. Reusing it means the review affordances the
 * /memory page already has (edit-then-approve, the diff of your edits,
 * reject-with-reason) arrive here for free and cannot drift into a second,
 * differently-behaved reviewer.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../common/Button";
import { FailureExplanation } from "../../../common/FailureExplanation";
import { ReviewPanel } from "../../../common/ReviewPanel";
import { toast } from "../../../../stores/toast";
import { useDockStore, type DockPanel } from "../../../../stores/dock";
import {
	emitProposalsChanged,
	fetchProposalPreview,
	proposalActionKeptOpen,
	ProposalGone,
	type ProposalPreview,
} from "../../../../lib/proposalsApi";
import { inspectorData } from "./data";
import styles from "./ProposalInspectorBody.module.css";

/** Drop every LEADING `---` fenced block, for display. The server already
 *  removed the file's own frontmatter; what can remain is the frontmatter of
 *  a document the proposal WRAPS (the pending relationship file carries the
 *  RELATIONSHIP.md it would write). Mirrors the server's excerpt rule. */
export function stripLeadingFrontmatter(text: string): string {
	const fence = /^\s*---\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/;
	let body = text;
	for (;;) {
		const m = fence.exec(body);
		if (!m) return body;
		body = body.slice(m[0].length);
	}
}

export function ProposalInspectorBody({ panel }: { panel: DockPanel }) {
	const proposalId = String(inspectorData(panel).props.proposalId ?? "");
	const [preview, setPreview] = useState<ProposalPreview | null>(null);
	const [gone, setGone] = useState("");
	const [busy, setBusy] = useState(false);
	const closePanel = useDockStore((s) => s.closePanel);

	const load = useCallback(async () => {
		setGone("");
		try {
			setPreview(await fetchProposalPreview(proposalId));
		} catch (e) {
			if (e instanceof ProposalGone) {
				setGone(e.message);
				setPreview(null);
				return;
			}
			toast.error(e instanceof Error ? e.message : "Couldn't load the proposal");
		}
	}, [proposalId]);

	useEffect(() => {
		void load();
	}, [load]);

	// One decision path for both verbs. `body` differs (edited content vs a
	// reason) but everything after the POST is identical, and writing it
	// twice is how the two buttons end up reporting success differently.
	const decide = async (
		url: string,
		body: Record<string, unknown>,
		past: string,
	) => {
		if (!url) {
			toast.error(`This proposal has no ${past.replace("ed", "")} action.`);
			return;
		}
		setBusy(true);
		try {
			const res = await fetch(url, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});
			const parsed = await res
				.json()
				.then((b) => b as Record<string, unknown>)
				.catch(() => ({} as Record<string, unknown>));
			if (!res.ok) {
				const detail = parsed?.detail;
				throw new Error(
					(typeof detail === "string" && detail)
						|| `The server refused it (${res.status})`,
				);
			}
			const keptOpen = proposalActionKeptOpen(parsed);
			if (keptOpen) {
				toast.warning(preview?.title ?? "Proposal", keptOpen);
				return;
			}
			toast.success(`${preview?.title ?? "Proposal"} ${past}`);
			emitProposalsChanged();
			closePanel(panel.id);
		} catch (e) {
			toast.error(e instanceof Error ? e.message : `Couldn't ${past} it`);
		} finally {
			setBusy(false);
		}
	};

	if (gone) {
		return (
			<div className={styles.empty}>
				<p>{gone}</p>
				<Button size="sm" variant="secondary" onClick={() => closePanel(panel.id)}>
					Close panel
				</Button>
			</div>
		);
	}
	if (!preview) return <div className={styles.empty}>Loading proposal…</div>;

	return (
		<div className={styles.body}>
			{/* Why this is in front of you, and which file it is. A strip, not
			    a box: the dock tab already carries the title, and the owner's
			    verdict on the first cut — a card inside the panel — was that
			    the document should show "directly". */}
			<div className={styles.intro}>
				{/* Rule 26: the identical block the list card renders, stamped
				    by the same server-side annotator.

				    No `actions` map DELIBERATELY — the two verbs below ARE this
				    panel's actions (approve the document, reject it), so there
				    is no third thing a remedy button could perform here. Same
				    reasoning as the /proposals and /inbox cards. */}
				{(preview.cause || (preview.semantics?.remedies?.length ?? 0) > 0) && (
					<FailureExplanation
						cause={preview.cause}
						remedies={preview.semantics?.remedies}
					/>
				)}
				<p className={styles.source}>{preview.path}</p>
			</div>
			<ReviewPanel
				variant="flush"
				title={preview.title}
				content={preview.content}
				// Display only — Edit still works on the raw text, because the
				// inner block is content that WILL be written. Rendered raw, a
				// fenced block is a rule plus a setext heading, which is how
				// "type: relationship updated: 2026-09-20" became the
				// document's largest line.
				processContent={stripLeadingFrontmatter}
				loading={busy}
				onApprove={(content) =>
					decide(preview.approve_url, { content }, "approved")
				}
				onReject={
					preview.reject_url
						? (reason) => decide(preview.reject_url, { reason }, "rejected")
						: undefined
				}
			/>
		</div>
	);
}
